import Vue from 'vue';
import URI from 'urijs'
import { config } from '@/plugins/env.config'
import axios from 'axios';
import { status, icons, types, orderBy } from '@/config/dynamic-authorization'

async function getAuthorizationList(filters, attemptNumber = 1) {
  const getAuthorizationsUri = new URI(config.apiBaseUrl)
  getAuthorizationsUri.segment('gnp-arc')
  getAuthorizationsUri.segment('v1')
  getAuthorizationsUri.segment('beneficiaries')
  getAuthorizationsUri.segment(filters.beneficiaryId)
  getAuthorizationsUri.segment('authorization-summaries')
  getAuthorizationsUri.addSearch('from_date', filters.fromDate)
  getAuthorizationsUri.addSearch('to_date', filters.toDate)
  getAuthorizationsUri.addSearch('statuses', filters.statuses)
  getAuthorizationsUri.addSearch('coverages', filters.types)
  getAuthorizationsUri.addSearch('page', filters.page)
  getAuthorizationsUri.addSearch('size', filters.size)
  getAuthorizationsUri.addSearch('authorizationCode', filters.authorizationCode)
  getAuthorizationsUri.addSearch('orderBy', filters.orderBy)
  getAuthorizationsUri.addSearch('sort', filters.sort)

  try {
    return await Vue.prototype.$http.get(getAuthorizationsUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationList(filters, attemptNumber + 1)
    }

    throw error
  }
  
}

async function getAuthorizationForm(sponsorSlug, attemptNumber = 1) {
  const getAuthorizationFormUri = new URI(config.apiBaseUrl)
  getAuthorizationFormUri.segment('gnp-arc')
  getAuthorizationFormUri.segment('v1')
  getAuthorizationFormUri.segment('sponsors')
  getAuthorizationFormUri.segment(sponsorSlug)
  getAuthorizationFormUri.segment('authorization-forms')

  try {
    return await Vue.prototype.$http.get(getAuthorizationFormUri.toString())
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationForm(sponsorSlug, attemptNumber + 1)
    }

    throw error
  }
}

async function getGuestAuthorizationForm(sponsorSlug, attemptNumber = 1) {
  const getAuthorizationFormUri = new URI(config.apiBaseUrl)
  getAuthorizationFormUri.segment('gnp-arc')
  getAuthorizationFormUri.segment('v1')
  getAuthorizationFormUri.segment('sponsors')
  getAuthorizationFormUri.segment(sponsorSlug)
  getAuthorizationFormUri.segment('authorization-forms')

  const headers = {
        Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }
  
  try {
    return await axios.get(getAuthorizationFormUri.toString(), { headers })
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getGuestAuthorizationForm(sponsorSlug, attemptNumber + 1)
    }

    throw error
  }
}

async function sendReimbursement(payload, attemptNumber = 1) {
  const sendReimbursementUri = new URI(config.apiBaseUrl)
  sendReimbursementUri.segment('gnp-arc')
  sendReimbursementUri.segment('v1')
  sendReimbursementUri.segment('reimbursements')

  try {
    return await Vue.prototype.$http.post(sendReimbursementUri.toString(), payload)
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendReimbursement(payload, attemptNumber + 1)
    }

    throw error
  }
}

async function sendGuestReimbursement(payload, attemptNumber = 1) {
  const sendReimbursementUri = new URI(config.apiBaseUrl)
  sendReimbursementUri.segment('gnp-arc')
  sendReimbursementUri.segment('v1')
  sendReimbursementUri.segment('reimbursements')

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  try {
    return await axios.post(sendReimbursementUri.toString(), payload, { headers })
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendGuestReimbursement(payload, attemptNumber + 1)
    }

    throw error
  }
}

async function sendAuthorization(payload, attemptNumber = 1) {
  const sendAuthorizationUri = new URI(config.apiBaseUrl)
  sendAuthorizationUri.segment('gnp-arc')
  sendAuthorizationUri.segment('v2')
  sendAuthorizationUri.segment('authorizations')

  try {
    return await Vue.prototype.$http.post(sendAuthorizationUri.toString(), payload)
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendAuthorization(payload, attemptNumber + 1)
    }

    throw error
  }
}

async function completeReimbursement(reimbursementId = '', attemptNumber = 1) {
  const completeReimbursementUri = new URI(config.apiBaseUrl)
  completeReimbursementUri.segment('gnp-arc')
  completeReimbursementUri.segment('v1')
  completeReimbursementUri.segment('reimbursements')
  completeReimbursementUri.segment(reimbursementId)
  completeReimbursementUri.segment('complete')

  try {
    return await Vue.prototype.$http.post(completeReimbursementUri.toString())
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return completeReimbursement(reimbursementId, attemptNumber + 1)
    }

    throw error
  }
}

async function completeGuestReimbursement(reimbursementId = '', attemptNumber = 1) {
  const completeReimbursementUri = new URI(config.apiBaseUrl)
  completeReimbursementUri.segment('gnp-arc')
  completeReimbursementUri.segment('v1')
  completeReimbursementUri.segment('reimbursements')
  completeReimbursementUri.segment(reimbursementId)
  completeReimbursementUri.segment('complete')

  const headers = {
        Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  try {
    return await axios.post(completeReimbursementUri.toString(), null, { headers })
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return completeGuestReimbursement(reimbursementId, attemptNumber + 1)
    }

    throw error
  }
}

async function completeAuthorization(authorizationId = '', attemptNumber = 1) {
  const completeAuthorizationUri = new URI(config.apiBaseUrl)
  completeAuthorizationUri.segment('gnp-arc')
  completeAuthorizationUri.segment('v2')
  completeAuthorizationUri.segment('authorizations')
  completeAuthorizationUri.segment(authorizationId.toString())
  completeAuthorizationUri.segment('complete')

  try {
    return await Vue.prototype.$http.post(completeAuthorizationUri.toString())
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return completeAuthorization(authorizationId, attemptNumber + 1)
    }

    throw error
  }
}

async function sendFile(fileSlug, file, reimbursementId = '', attemptNumber = 1) {
  const sendFileFormUri = new URI(config.apiBaseUrl)
  sendFileFormUri.segment('gnp-arc')
  sendFileFormUri.segment('v1')
  sendFileFormUri.segment('reimbursements')
  sendFileFormUri.segment(reimbursementId)
  sendFileFormUri.segment('inputs')
  sendFileFormUri.segment(fileSlug)

  const formData = new FormData();
  formData.append('file', file);
 
  try {
    return await Vue.prototype.$http
      .post(sendFileFormUri.toString(), formData, {})
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendFile(fileSlug, file, reimbursementId, attemptNumber + 1)
    }

    throw error
  }
}

async function sendGuestFile(fileSlug, file, reimbursementId = '', attemptNumber = 1) {
  const sendFileFormUri = new URI(config.apiBaseUrl)
  sendFileFormUri.segment('gnp-arc')
  sendFileFormUri.segment('v1')
  sendFileFormUri.segment('reimbursements')
  sendFileFormUri.segment(reimbursementId)
  sendFileFormUri.segment('inputs')
  sendFileFormUri.segment(fileSlug)

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  const formData = new FormData();
  formData.append('file', file);
 
  try {
    return await axios
      .post(sendFileFormUri.toString(), formData, { headers })
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendGuestFile(fileSlug, file, reimbursementId, attemptNumber + 1)
    }

    throw error
  }
}

async function sendAuthorizationFile(fileSlug, file, authorizationId = '', attemptNumber = 1) {
  const sendFileFormUri = new URI(config.apiBaseUrl)
  sendFileFormUri.segment('gnp-arc')
  sendFileFormUri.segment('v2')
  sendFileFormUri.segment('authorizations')
  sendFileFormUri.segment(authorizationId.toString())
  sendFileFormUri.segment('inputs')
  sendFileFormUri.segment(fileSlug)

  const formData = new FormData();
  formData.append('file', file);
 
  try {
    return await Vue.prototype.$http
      .post(sendFileFormUri.toString(), formData, {})
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendAuthorizationFile(fileSlug, file, authorizationId, attemptNumber + 1)
    }

    throw error
  }
}

async function getAuthorization(beneficiaryId, authorizationCode, attemptNumber = 1) {
  const getAuthorizationUri = new URI(config.apiBaseUrl)
  getAuthorizationUri.segment('gnp-arc')
  getAuthorizationUri.segment('v1')
  getAuthorizationUri.segment('beneficiaries')
  getAuthorizationUri.segment(beneficiaryId)
  getAuthorizationUri.segment('authorizations')
  getAuthorizationUri.segment(authorizationCode)
  getAuthorizationUri.segment('details')
  
  try {
    return await Vue.prototype.$http.get(getAuthorizationUri.toString())
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorization(beneficiaryId, authorizationCode, attemptNumber + 1)
    }

    throw error
  }
}

async function getGuestAuthorization(authorizationCode, attemptNumber = 1) {
  const getAuthorizationUri = new URI(config.apiBaseUrl)
  getAuthorizationUri.segment('gnp-arc')
  getAuthorizationUri.segment('v2')
  getAuthorizationUri.segment('authorizations')
  getAuthorizationUri.segment(authorizationCode)
  getAuthorizationUri.segment('details')
  
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  try {
    return await axios.get(getAuthorizationUri.toString(), { headers })
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getGuestAuthorization(authorizationCode, attemptNumber + 1)
    }

    throw error
  }
}

async function getAuthorizationFile(fileId, attemptNumber = 1) {
  const getAuthorizationFileUri = new URI(config.apiBaseUrl)
  getAuthorizationFileUri.segment('gnp-arc')
  getAuthorizationFileUri.segment('v1')
  getAuthorizationFileUri.segment('reimbursements')
  getAuthorizationFileUri.segment('files')
  getAuthorizationFileUri.segment(fileId)

  try {
    return await Vue.prototype.$http.get(getAuthorizationFileUri.toString(), {responseType: 'arraybuffer'})
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getAuthorizationFile(fileId, attemptNumber + 1)
    }

    throw error
  }
}

async function sendComment(authorizationCode, payload, attemptNumber = 1) {
  const sendCommentUri = new URI(config.apiBaseUrl)
  sendCommentUri.segment('gnp-arc')
  sendCommentUri.segment('v1')
  sendCommentUri.segment('authorizations')
  sendCommentUri.segment(authorizationCode)
  sendCommentUri.segment('comment')

  try {
    return await Vue.prototype.$http.post(sendCommentUri.toString(), payload)
  } catch (error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return sendComment(authorizationCode, payload, attemptNumber + 1)
    }

    throw error
  }
}

function getStatus(authorization) {
  const statusByCoverage = status.find(configuration => configuration.coverageType === authorization.coverageType)
  if (statusByCoverage) {
    const configurationState = statusByCoverage.states.find(state => state.value === authorization.status);
    if(configurationState){
      return configurationState && configurationState.text || '';
    }
  }
  return getStatus({ coverageType: 'DEFAULT', status: authorization.status })
}

function getType(authorizationType) {
   const configurationType = types.find(type => type.slug === authorizationType);
   return configurationType && configurationType.text || '';
}

function getTypeObject(authorizationType) {
  const configurationType = types.find(type => type.slug === authorizationType);
  return configurationType;
}

function getColor(authorization) {
  const statusByCoverage = status.find(configuration => configuration.coverageType === authorization.coverageType)
  if (statusByCoverage) {
    const configurationState = statusByCoverage.states.find(state => state.value === authorization.status);
    return configurationState || {};
  }
  return getColor({ coverageType: 'DEFAULT', status: authorization.status })
}

function getIcon(productStatus) {
  const configurationState = icons.find(icon => icon.productStatus === productStatus);
  return configurationState ? configurationState : {};
}

async function setAuthorizationToErrorStatus(authorizationId = '', attemptNumber = 1){
  const errorAuthorizationURI = new URI(config.apiBaseUrl)
  errorAuthorizationURI.segment('gnp-arc')
  errorAuthorizationURI.segment('v2')
  errorAuthorizationURI.segment('authorizations')
  errorAuthorizationURI.segment(authorizationId.toString())
  errorAuthorizationURI.segment('error')

  try {
    return await Vue.prototype.$http.post(errorAuthorizationURI.toString())
  } catch(error) {
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return setAuthorizationToErrorStatus(authorizationId, attemptNumber + 1)
    }

    throw error
  }
}

async function setReimbursementToErrorStatus(reimbursementId = '', attemptNumber = 1){
  const errorReimbursementUri = new URI(config.apiBaseUrl)
  errorReimbursementUri.segment('gnp-arc')
  errorReimbursementUri.segment('v1')
  errorReimbursementUri.segment('reimbursements')
  errorReimbursementUri.segment(reimbursementId)
  errorReimbursementUri.segment('error')
  
  try{
    return await Vue.prototype.$http.post(errorReimbursementUri.toString())
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return setReimbursementToErrorStatus(reimbursementId, attemptNumber + 1)
    }

    throw error
  }

}

async function setGuestReimbursementToErrorStatus(reimbursementId = '', attemptNumber = 1){
  const errorReimbursementUri = new URI(config.apiBaseUrl)
  errorReimbursementUri.segment('gnp-arc')
  errorReimbursementUri.segment('v1')
  errorReimbursementUri.segment('reimbursements')
  errorReimbursementUri.segment(reimbursementId)
  errorReimbursementUri.segment('error')
  
  const headers = {
    Authorization: `Bearer ${localStorage.getItem('clientToken')}`
  }

  try{
    return await axios.post(errorReimbursementUri.toString(), null, {headers})
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    const status = response.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return setGuestReimbursementToErrorStatus(reimbursementId, attemptNumber + 1)
    }

    throw error
  }

}

export default {
  getAuthorizationList,
  getAuthorizationForm,
  getStatus,
  getType,
  getColor,
  getIcon,
  sendFile,
  sendAuthorizationFile,
  sendReimbursement,
  sendAuthorization,
  getTypeObject,
  completeReimbursement,
  completeAuthorization,
  getAuthorization,
  getAuthorizationFile,
  sendComment,
  setAuthorizationToErrorStatus,
  setReimbursementToErrorStatus,
  status,
  types,
  orderBy,
  getGuestAuthorizationForm,
  sendGuestReimbursement,
  completeGuestReimbursement,
  sendGuestFile,
  setGuestReimbursementToErrorStatus,
  getGuestAuthorization
}